// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * ⌘Z's order in Rediger › Geometri (frontend-u0o.11). The model's edits
 * are undone on the server (`POST /model/undo`); the guides and dimension
 * chains, which live in this browser, by their own history
 * (`cadAnnotations.ts`). This journal remembers which of the two each of
 * this session's edits touched, so ⌘Z takes back the newest edit whichever
 * kind it was — and a Delete of both at once as one step. With nothing of
 * this session's left, ⌘Z falls through to the server (an edit made
 * before). Pure: no React, no DOM.
 */

export interface JournalEntry {
  id: number;
  /** The guides / chains changed: their history steps back one. */
  annotations: boolean;
  /** The model changed: the server undoes one batch. */
  model: boolean;
}

export interface Journal {
  /** Oldest first. */
  undo: JournalEntry[];
  /** The next redo last. */
  redo: JournalEntry[];
  seq: number;
}

export const EMPTY_JOURNAL: Journal = { undo: [], redo: [], seq: 1 };

/** How many steps the journal keeps (the server and the list keep their own). */
const LIMIT = 200;

/** An edit made: it goes on top, and what was undone can no longer be redone. */
export function record(j: Journal, parts: { annotations?: boolean; model?: boolean }): { journal: Journal; id: number } {
  const entry: JournalEntry = { id: j.seq, annotations: parts.annotations ?? false, model: parts.model ?? false };
  return { journal: { undo: [...j.undo, entry].slice(-LIMIT), redo: [], seq: j.seq + 1 }, id: entry.id };
}

/** Entry `id`'s model half landed (a mixed delete's batch the server took after the list had changed). */
export function markModel(j: Journal, id: number): Journal {
  const mark = (list: JournalEntry[]) => list.map((e) => (e.id === id ? { ...e, model: true } : e));
  return { ...j, undo: mark(j.undo), redo: mark(j.redo) };
}

/** ⌘Z: the newest entry (null: none of ours — the server's own undo). */
export function takeUndo(j: Journal): { journal: Journal; entry: JournalEntry | null } {
  const entry = j.undo[j.undo.length - 1];
  if (!entry) return { journal: j, entry: null };
  return { journal: { ...j, undo: j.undo.slice(0, -1), redo: [...j.redo, entry] }, entry };
}

/** ⇧⌘Z: the last undone entry (null: none of ours — the server's own redo). */
export function takeRedo(j: Journal): { journal: Journal; entry: JournalEntry | null } {
  const entry = j.redo[j.redo.length - 1];
  if (!entry) return { journal: j, entry: null };
  return { journal: { ...j, redo: j.redo.slice(0, -1), undo: [...j.undo, entry] }, entry };
}
